import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";

import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import { useEmpresa } from "../../contexts/EmpresaContext";
import { nivelUsuario } from "../../utils/permissoes";
import { NIVEIS } from "../../config/niveis";
import LayoutDesktop from "../../layouts/LayoutDesktop";
import FiltroBar from "../../components/FiltroBar/FiltroBar";
import { useSortableTable, SortableHeader } from "../../hooks/useSortableTable";
import { formatData } from "../../utils/dateFormat";
import { db } from "../../config/firebase";

const COLLECTION_PROPOSTAS_OS = "financeiro_propostas_os";
const COLLECTION_SESSOES = "atendimentos_sessoes";

const STATUS_OPTIONS = [
  { value: "CRIADA", label: "Proposta Criada" },
  { value: "ENVIADA", label: "Enviada" },
  { value: "APROVADA", label: "Aprovada" },
  { value: "REPROVADA", label: "Reprovada" },
  { value: "FINALIZADA", label: "Finalizada" },
];

function formatMoney(value) {
  const n = Number(value || 0);
  return n.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function FinanceiroOrdensServicoPage() {
  const { tema } = useTheme();
  const { usuario } = useAuth();
  const { empresaAtual, modoGlobal } = useEmpresa();
  const navigate = useNavigate();

  const [registros, setRegistros] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState("");
  const [campoFiltro, setCampoFiltro] = useState("clienteNome");
  const [textoFiltro, setTextoFiltro] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [deletandoId, setDeletandoId] = useState(null);
  const [salvandoStatusId, setSalvandoStatusId] = useState(null);

  useEffect(() => {
    const carregar = async () => {
      if (!usuario?.grupoId) return;
      setCarregando(true);
      setErro("");
      try {
        const nivel = nivelUsuario(usuario);
        const restringirEmpresa = !modoGlobal || nivel < NIVEIS.TECNICO_SENIOR;

        let qBase = query(
          collection(db, COLLECTION_PROPOSTAS_OS),
          where("grupoId", "==", usuario.grupoId)
        );

        if (restringirEmpresa) {
          const empresaId = empresaAtual?.id || usuario?.empresaId || null;
          if (empresaId) {
            qBase = query(
              collection(db, COLLECTION_PROPOSTAS_OS),
              where("grupoId", "==", usuario.grupoId),
              where("clienteId", "==", empresaId)
            );
          }
        }

        const snap = await getDocs(qBase);
        const lista = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setRegistros(lista);
      } catch (e) {
        console.error("Erro ao carregar propostas de OS:", e);
        setErro("Erro ao carregar propostas de OS.");
        setRegistros([]);
      } finally {
        setCarregando(false);
      }
    };

    carregar();
  }, [usuario?.grupoId, usuario?.empresaId, empresaAtual?.id, modoGlobal]);

  const camposFiltro = [
    { value: "clienteNome", label: "Cliente" },
    { value: "academiaNome", label: "Academia" },
    { value: "status", label: "Status" },
    { value: "formaPagamento", label: "Forma pagamento" },
  ];

  const filtrados = useMemo(() => {
    const texto = textoFiltro.trim().toLowerCase();
    if (!texto) return registros;

    return registros.filter((r) => {
      const base = String(r?.[campoFiltro] ?? "").toLowerCase();
      return base.includes(texto);
    });
  }, [registros, campoFiltro, textoFiltro]);

  const { sortedData, requestSort, getSortDirection } = useSortableTable(
    filtrados,
    "clienteNome"
  );

  const handleExcluir = async (registro) => {
    if (!registro?.id) return;
    const confirmar = window.confirm("Deseja excluir esta proposta de OS?");
    if (!confirmar) return;

    setDeletandoId(registro.id);
    setErro("");
    try {
      await deleteDoc(doc(db, COLLECTION_PROPOSTAS_OS, registro.id));
      setRegistros((prev) => prev.filter((item) => item.id !== registro.id));
    } catch (e) {
      console.error("Erro ao excluir proposta:", e);
      setErro("Erro ao excluir proposta de OS.");
    } finally {
      setDeletandoId(null);
    }
  };

  const atualizarStatus = async (registro, novoStatus) => {
    if (!registro?.id) return;
    setSalvandoStatusId(registro.id);
    setErro("");

    try {
      await updateDoc(doc(db, COLLECTION_PROPOSTAS_OS, registro.id), {
        status: novoStatus,
        updatedAt: serverTimestamp(),
      });

      if (registro?.sessaoId) {
        await updateDoc(doc(db, COLLECTION_SESSOES, registro.sessaoId), {
          propostaStatus: novoStatus,
          liberadaAgendamento: String(novoStatus).toUpperCase() === "APROVADA",
          updatedAt: serverTimestamp(),
        });
      }

      setRegistros((prev) =>
        prev.map((item) =>
          item.id === registro.id ? { ...item, status: novoStatus } : item
        )
      );
    } catch (e) {
      console.error("Erro ao atualizar status:", e);
      setErro("Erro ao atualizar status da proposta.");
    } finally {
      setSalvandoStatusId(null);
    }
  };

  return (
    <LayoutDesktop
      tema={tema}
      titulo="Propostas"
      subtitulo="Financeiro / Propostas / Ordens de Servico"
    >
      <section className="pc-card pc-table-card">
        <header className="pc-table-header">
          <div>
            <h3>Propostas de OS</h3>
            <p>Crie, acompanhe e aprove propostas de visitas avulsas.</p>
          </div>
          <button
            type="button"
            className="pc-btn-primario"
            onClick={() => navigate("/financeiro/propostas-os/nova")}
          >
            Nova proposta
          </button>
        </header>

        <FiltroBar
          campos={camposFiltro}
          campoSelecionado={campoFiltro}
          onCampoChange={setCampoFiltro}
          valorBusca={textoFiltro}
          onBuscaChange={setTextoFiltro}
          placeholder="Filtrar propostas..."
        />

        {erro && <div className="pc-alert pc-alert-erro">{erro}</div>}

        <div className="pc-table-wrapper">
          <table className="pc-table">
            <thead>
              <tr>
                <SortableHeader
                  label="Cliente"
                  columnKey="clienteNome"
                  onSort={requestSort}
                  direction={getSortDirection("clienteNome")}
                />
                <SortableHeader
                  label="Academia"
                  columnKey="academiaNome"
                  onSort={requestSort}
                  direction={getSortDirection("academiaNome")}
                />
                <SortableHeader
                  label="Status"
                  columnKey="status"
                  onSort={requestSort}
                  direction={getSortDirection("status")}
                />
                <SortableHeader
                  label="Valor"
                  columnKey="valorTotal"
                  onSort={requestSort}
                  direction={getSortDirection("valorTotal")}
                />
                <SortableHeader
                  label="Pagamento"
                  columnKey="dataPagamento"
                  onSort={requestSort}
                  direction={getSortDirection("dataPagamento")}
                />
                <th>Acoes</th>
              </tr>
            </thead>
            <tbody>
              {carregando ? (
                <tr>
                  <td colSpan={6} style={{ padding: 16 }}>
                    Carregando propostas...
                  </td>
                </tr>
              ) : sortedData.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ padding: 16 }}>
                    Nenhuma proposta encontrada.
                  </td>
                </tr>
              ) : (
                sortedData.map((registro) => {
                  const aberto = expandedId === registro.id;
                  return (
                    <React.Fragment key={registro.id}>
                      <tr>
                        <td>{registro.clienteNome || "-"}</td>
                        <td>{registro.academiaNome || "-"}</td>
                        <td>
                          <select
                            value={registro.status || "CRIADA"}
                            className="pc-input"
                            disabled={salvandoStatusId === registro.id}
                            onChange={(e) => atualizarStatus(registro, e.target.value)}
                            style={{ minWidth: 140 }}
                          >
                            {STATUS_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>R$ {formatMoney(registro.valorTotal)}</td>
                        <td>{registro.dataPagamento || "-"}</td>
                        <td>
                          <div style={{ display: "flex", gap: 8 }}>
                            <button
                              type="button"
                              className="pc-btn-secundario"
                              onClick={() => setExpandedId(aberto ? null : registro.id)}
                            >
                              {aberto ? "Ocultar" : "Detalhes"}
                            </button>
                            <button
                              type="button"
                              className="pc-btn-secundario"
                              onClick={() => navigate(`/financeiro/propostas-os/editar/${registro.id}`)}
                            >
                              Editar
                            </button>
                            <button
                              type="button"
                              className="pc-btn-perigo"
                              disabled={deletandoId === registro.id}
                              onClick={() => handleExcluir(registro)}
                            >
                              {deletandoId === registro.id ? "Excluindo..." : "Excluir"}
                            </button>
                          </div>
                        </td>
                      </tr>
                      {aberto && (
                        <tr>
                          <td colSpan={6} style={{ background: "rgba(15,23,42,0.35)" }}>
                            <div style={{ padding: 16, display: "grid", gap: 8 }}>
                              <div>
                                <strong>OS vinculadas:</strong>
                                <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                                  {(registro.osResumo || []).length ? (
                                    registro.osResumo.map((os) => (
                                      <li key={os.osId || os.osCodigo}>
                                        {os.osCodigo ? `#${os.osCodigo} - ` : ""}
                                        {os.osTitulo || os.osTipoServico || os.osId}
                                      </li>
                                    ))
                                  ) : (
                                    <li>Nenhuma OS informada.</li>
                                  )}
                                </ul>
                              </div>
                              <div>
                                <strong>Forma de pagamento:</strong> {registro.formaPagamento || "-"}
                              </div>
                              <div>
                                <strong>Observacoes:</strong> {registro.observacoes || "-"}
                              </div>
                              <div>
                                <strong>Criado em:</strong> {formatData(registro.createdAt)}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>
    </LayoutDesktop>
  );
}
